import React, { useState, useEffect, useCallback } from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  PersonSearch as PersonSearchIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import ClientSelectionDialog from "./ClientSelectionDialog";
import InstallmentDetailsDialog from "./InstallmentDetailsDialog";
import axios from "axios";
import config from "./global/config";
import { tokens } from "../dashboard/theme";
import { useTheme } from "@mui/material/styles";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { Delete as DeleteIcon } from "@mui/icons-material";

const createApiInstance = () => {
  const api = axios.create({
    baseURL: config.apiUrl,
    withCredentials: true,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return api;
};

const InstallmentListPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [state, setState] = useState({
    isClientDialogOpen: false,
    isDetailsDialogOpen: false,
    selectedClient: null,
    selectedInstallmentId: null,
    installments: [],
    clients: [],
    loading: false,
    error: null,
    isDeleteDialogOpen: false,
    installmentToDelete: null,
    snackbar: {
      open: false,
      message: "",
      severity: "success",
    },
  });

  const api = createApiInstance();
  const handleCloseSnackbar = () => {
    setState((prev) => ({
      ...prev,
      snackbar: {
        ...prev.snackbar,
        open: false,
      },
    }));
  };
  const fetchInstallments = useCallback(async (clientId) => {
    if (!clientId) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const salesResponse = await api.get(`ventes.php?id_client=${clientId}`);
      const sales = salesResponse.data;

      const installmentsPromises = sales.map((sale) =>
        api.get(`paiements_clients_tranches.php?id_vente=${sale.id_vente}`)
      );

      const installmentsResponses = await Promise.all(installmentsPromises);
      const allInstallments = installmentsResponses
        .flatMap((response) => response.data)
        .filter(Boolean);

      // Force refresh of the data
      setState((prev) => ({
        ...prev,
        installments: [], // Clear first to force re-render
        loading: false,
      }));

      // Then set the new data
      setState((prev) => ({
        ...prev,
        installments: allInstallments,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: "Error fetching installments. Please try again later.",
      }));
      //console.error("Error fetching installments:", error);
    }
  }, []);

  const handleDeleteInstallment = async () => {
    if (!state.installmentToDelete) return;

    try {
      const response = await api.post(
        `paiements_clients_tranches.php?id_paiement_tranche=${state.installmentToDelete}`,
        { METHOD: "DELETE" },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.data.success) {
        setState((prev) => ({
          ...prev,
          isDeleteDialogOpen: false,
          installmentToDelete: null,
          snackbar: {
            open: true,
            message: "Suppression effectuée avec succès",
            severity: "success",
          },
        }));

        if (state.selectedClient) {
          fetchInstallments(state.selectedClient.id_client);
        }
      } else {
        throw new Error(response.data.error || "Erreur lors de la suppression");
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        snackbar: {
          open: true,
          message:
            error.response?.data?.error ||
            "Erreur lors de la suppression. Veuillez réessayer.",
          severity: "error",
        },
      }));
    }
  };
  const handleDetailsDialogClose = useCallback(() => {
    setState((prev) => ({ ...prev, isDetailsDialogOpen: false }));

    // Immediately refresh the data
    if (state.selectedClient) {
      fetchInstallments(state.selectedClient.id_client);
    }
  }, [state.selectedClient, fetchInstallments]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await api.get("client.php");
      setState((prev) => ({ ...prev, clients: response.data }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: "Error fetching clients. Please try again later.",
      }));
      //console.error("Error fetching clients:", error);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (state.selectedClient) {
      fetchInstallments(state.selectedClient.id_client);
    }
  }, [state.selectedClient, fetchInstallments]);

  const handleClientSelect = useCallback((client) => {
    setState((prev) => ({
      ...prev,
      selectedClient: client,
      isClientDialogOpen: false,
    }));
  }, []);

  const columns = [
    {
      field: "numero_paiement",
      headerName: "N° Paiement",
      flex: 0.6,
      cellClassName: "name-column--cell",
    },
    {
      field: "nombre_tranches",
      headerName: "Nombre de tranches",
      flex: 0.6,
    },
    {
      field: "montant_total",
      headerName: "Montant total",
      flex: 0.6,
      valueFormatter: (params) =>
        params.value?.toLocaleString("fr-FR", {
          style: "currency",
          currency: "MAD",
        }),
    },
    {
      field: "montant_par_tranche",
      headerName: "Montant/tranche",
      flex: 0.6,
      valueFormatter: (params) =>
        params.value?.toLocaleString("fr-FR", {
          style: "currency",
          currency: "MAD",
        }),
    },
    {
      field: "date_debut",
      headerName: "Date début",
      flex: 0.6,
    },
    {
      field: "periodicite_jours",
      headerName: "Périodicité (jours)",
      flex: 0.3,
    },
    {
      field: "statut_tranches",
      headerName: "Statut",
      flex: 1,
      renderCell: ({ row: { statut_tranches } }) => (
        <Box
          width="60%"
          m="0 auto"
          p="5px"
          display="flex"
          justifyContent="center"
          backgroundColor={
            statut_tranches === "Terminé"
              ? colors.greenAccent[600]
              : colors.blueAccent[700]
          }
          borderRadius="4px"
        >
          <Typography color={colors.grey[100]} sx={{ ml: "10px" }}>
            {statut_tranches}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<VisibilityIcon />}
            onClick={() =>
              setState((prev) => ({
                ...prev,
                selectedInstallmentId: params.row.id_paiement_tranche,
                isDetailsDialogOpen: true,
              }))
            }
            size="small"
          >
            Détails
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() =>
              setState((prev) => ({
                ...prev,
                isDeleteDialogOpen: true,
                installmentToDelete: params.row.id_paiement_tranche,
              }))
            }
            size="small"
          >
            Supprimer
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          variant="h2"
          color={colors.grey[100]}
          fontWeight="bold"
          sx={{ mb: "5px" }}
        >
          Liste des Paiements en Tranches
        </Typography>

        <Button
          variant="contained"
          color="secondary"
          startIcon={<PersonSearchIcon />}
          onClick={() =>
            setState((prev) => ({ ...prev, isClientDialogOpen: true }))
          }
          sx={{ mb: "20px" }}
        >
          {state.selectedClient
            ? `Client: ${state.selectedClient.nom} ${state.selectedClient.prenom}`
            : "Sélectionner un Client"}
        </Button>
      </Box>

      {state.error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {state.error}
        </Typography>
      )}

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.secondary,
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          rows={state.installments}
          columns={columns}
          getRowId={(row) => row.id_paiement_tranche}
          loading={state.loading}
          disableSelectionOnClick
        />
      </Box>

      <ClientSelectionDialog
        open={state.isClientDialogOpen}
        onClose={() =>
          setState((prev) => ({ ...prev, isClientDialogOpen: false }))
        }
        onSelect={handleClientSelect}
        clients={state.clients}
      />

      <InstallmentDetailsDialog
        open={state.isDetailsDialogOpen}
        onClose={handleDetailsDialogClose}
        installmentId={state.selectedInstallmentId}
        colors={colors}
        onUpdate={() => {
          if (state.selectedClient) {
            fetchInstallments(state.selectedClient.id_client);
          }
        }}
      />
      <DeleteConfirmationDialog
        open={state.isDeleteDialogOpen}
        onClose={() =>
          setState((prev) => ({
            ...prev,
            isDeleteDialogOpen: false,
            installmentToDelete: null,
          }))
        }
        onConfirm={handleDeleteInstallment}
        title="Confirmer la suppression"
        content="Êtes-vous sûr de vouloir supprimer ce paiement en tranches ? Cette action supprimera également tous les règlements associés et ne peut pas être annulée."
      />
      <Snackbar
        open={state.snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={state.snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {state.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InstallmentListPage;
